/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  useState,
  useCallback,
  type FormEvent,
  type FormEventHandler,
  type FC,
} from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { useTranslation } from "react-i18next";
import { Heading, Text, Button } from "@vector-im/compound-web";
import { logger } from "matrix-js-sdk/lib/logger";
import { useNavigate } from "react-router-dom";

import {
  createRoom,
  getRelativeRoomUrl,
  roomAliasLocalpartFromRoomName,
  sanitiseRoomNameInput,
} from "../utils/matrix";
import { useGroupCallRooms } from "./useGroupCallRooms";
import { Header, HeaderLogo, LeftNav, RightNav } from "../Header";
import commonStyles from "./common.module.css";
import styles from "./RegisteredView.module.css";
import { FieldRow, InputField, ErrorMessage } from "../input/Input";
import { CallList } from "./CallList";
import { UserMenuContainer } from "../UserMenuContainer";
import { JoinExistingCallModal } from "./JoinExistingCallModal";
import { Form } from "../form/Form";
import { AnalyticsNotice } from "../analytics/AnalyticsNotice";
import { E2eeType } from "../e2ee/e2eeType";
import { useOptInAnalytics } from "../settings/settings";
import { useUrlParams } from "../UrlParams";
import { ThemeToggle } from "../ThemeToggle";
import { backendFetch } from "../utils/backend";

interface Props {
  client: MatrixClient;
  isBoss?: boolean; // Добавлено
  onOpenAdminModal?: () => void; // Добавлено
}

// Генератор случайного ID (например: 4f8a2)
const generateRandomId = () => Math.random().toString(36).substring(2, 7);

export const RegisteredView: FC<Props> = ({
  client,
  isBoss,
  onOpenAdminModal,
}) => {
  const { header } = useUrlParams();
  // Разделяем состояние загрузки
  const [loadingAction, setLoadingAction] = useState<"none" | "submit">("none");
  const [error, setError] = useState<Error>();
  const [optInAnalytics] = useOptInAnalytics();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [joinExistingCallModalOpen, setJoinExistingCallModalOpen] =
    useState(false);
  const [existingAlias, setExistingAlias] = useState<string>();

  const onDismissJoinExistingCallModal = useCallback(
    () => setJoinExistingCallModalOpen(false),
    [setJoinExistingCallModalOpen],
  );

  const recentRooms = useGroupCallRooms(client);

  const onJoinExistingRoom = useCallback(() => {
    navigate(`/${existingAlias}`)?.catch((error) => {
      logger.error("Failed to navigate to existing alias", error);
    });
  }, [navigate, existingAlias]);

  // Универсальная функция создания звонка
  const createAndNavigate = useCallback(
    async (roomName: string) => {
      setError(undefined);
      setLoadingAction("submit");

      try {
        const createRoomResult = await createRoom(
          client,
          roomName,
          E2eeType.SHARED_KEY,
        );
        if (!createRoomResult.password) {
          throw new Error("Failed to create room with shared secret");
        }
        await backendFetch(client, "/api/rooms", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ matrixRoomId: createRoomResult.roomId }),
        });

        await navigate(
          getRelativeRoomUrl(
            createRoomResult.roomId,
            { kind: E2eeType.SHARED_KEY, secret: createRoomResult.password },
            roomName,
          ),
        );
      } catch (err: any) {
        if (err.errcode === "M_ROOM_IN_USE") {
          setExistingAlias(roomAliasLocalpartFromRoomName(roomName));
          setJoinExistingCallModalOpen(true);
        } else {
          logger.error(err);
          setError(err);
        }
        setLoadingAction("none"); // Сбрасываем загрузку
      }
    },
    [client, navigate],
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const data = new FormData(e.target as HTMLFormElement);
      const roomNameData = data.get("callName");
      const roomName =
        typeof roomNameData === "string"
          ? sanitiseRoomNameInput(roomNameData)
          : "";

      void createAndNavigate(roomName.trim() || `Call-${generateRandomId()}`);
    },
    [createAndNavigate],
  );

  return (
    <>
      <div className={commonStyles.container}>
        {header === "standard" && (
          <Header>
            <LeftNav hideMobile>
              <HeaderLogo />
            </LeftNav>
            <RightNav
              hideMobile
              className={styles.actions}
              style={{ display: "flex" }}
            >
              {/* Кнопка создания сотрудника */}
              {isBoss && (
                <button
                  onClick={onOpenAdminModal}
                  className="glass-button header-action"
                >
                  {t("instance.registered_view.create_user")}
                </button>
              )}

              <ThemeToggle />
              <UserMenuContainer />
            </RightNav>
          </Header>
        )}
        <main className={commonStyles.main}>
          <HeaderLogo className={commonStyles.logo} />
          <Heading size="lg" weight="semibold">
            {t("start_new_call")}
          </Heading>

          <Form className={styles.form} onSubmit={onSubmit}>
            <FieldRow className={styles.fieldRow}>
              <InputField
                id="callName"
                name="callName"
                label={t("call_name")}
                placeholder={t("call_name")}
                type="text"
                autoComplete="off"
                data-testid="home_callName"
              />

              <Button
                type="submit"
                size="lg"
                className={styles.button}
                disabled={loadingAction !== "none"}
                data-testid="home_go"
              >
                {loadingAction === "submit"
                  ? t("common.loading")
                  : t("action.go")}
              </Button>
            </FieldRow>

            {optInAnalytics === null && (
              <Text size="sm" className={styles.notice}>
                <AnalyticsNotice />
              </Text>
            )}
            {error && (
              <FieldRow className={styles.fieldRow}>
                <ErrorMessage error={error} />
              </FieldRow>
            )}
          </Form>
          <CallList rooms={recentRooms} client={client} isOwner={isBoss} />
        </main>
      </div>
      <JoinExistingCallModal
        onJoin={onJoinExistingRoom}
        open={joinExistingCallModalOpen}
        onDismiss={onDismissJoinExistingCallModal}
      />
    </>
  );
};
